import { useState } from 'react';
import {
  useCanAccess,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'ra-core';
import { creerUneRelation } from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { deballer, lireLErreur } from '../../fournisseurs/client';
import { useStructureCourante } from '../../fournisseurs/structure-courante';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **METTRE UNE PERSONNE EN RELATION — LE DERNIER MAILLON DE LA CHAÎNE, ET IL A DEMANDÉ
 * UN AMENDEMENT DU CAHIER DES CHARGES. D-099 puis D-101.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `POST /api/v1/provisionnement/relations` est livrée depuis le **LOT-1**, et **aucune surface
 * ne l'appelait**. Cet écran a été écrit, **puis éprouvé au navigateur**, et la route a
 * refusé : « une relation porte toujours une structure créatrice, et l'appelant n'en désigne
 * aucune ».
 *
 * **TROIS DÉCLARATIONS NE POUVAIENT PAS ÊTRE VRAIES ENSEMBLE — D-099.** `CDC-1` §7 ouvrait la
 * route à « secret de structure, `superadmin` » ; F-08 exige qu'une relation ait « **toujours
 * un créateur** : l'appel d'une structure » ; et Q535 pose que le compte qui voit tout **n'a
 * jamais de rattachement**. Le service refusait, **en appliquant F-08**. Et la matrice fermant
 * le geste à l'`orgadmin`, **aucun rôle humain ne pouvait créer une relation** : le produit ne
 * s'ouvrait à personne sans un appel du CRM.
 *
 * **AMENDÉ LE 20260826 PAR LE PORTEUR — D-101 : LE GESTE EST CELUI DE L'`orgadmin`.** Trois
 * raisons, et la deuxième tranche : F-08 permet déjà que « l'appel vienne de l'une ou l'autre
 * structure, indifféremment » ; **F-09 lui donne déjà le geste inverse**, et « pouvoir défaire
 * sans pouvoir faire est une asymétrie que rien ne justifie » ; et sa structure se déduit de
 * son rattachement, donc **aucun paramètre facultatif à borner** — ce qui rendait dangereuse
 * l'autre issue. `organisationAppelante()` la rendait déjà : **le service n'a pas changé d'une
 * ligne.**
 *
 * **LE COMPTE QUI VOIT TOUT N'A PLUS CE GESTE**, conséquence mécanique du choix : le garder
 * aurait laissé sur cette route le défaut exact que D-077 nomme. **Il garde le geste inverse**,
 * F-09 lui donnant `DELETE` sur toutes les relations. C'est pourquoi ce bloc **ne paraît pas
 * sur sa surface** — la matrice y répond `false`, et `useCanAccess` le lit.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA PAIRE EST UN TUTEUR ET UN ÉLÈVE, ET LE CORPUS N'EN EXPOSE PAS D'AUTRE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * DD-25 : « les paires possibles sont celles que le cadrage énumère : **tuteur avec élève**, et
 * élève avec élève **après un cours groupé partagé**. **Le corpus n'expose aucune relation
 * entre deux tuteurs.** » Et la paire élève-élève « naît d'un cours groupé, donc de `CDC-2` :
 * cette route-ci prend un tuteur et un élève, nommés comme tels ».
 *
 * **Cet écran ne propose donc que le rôle opposé**, et le serveur refuse le reste avec
 * `PAIRE_DE_RELATION_NON_EXPOSEE`. **Le filtrage ici n'est pas la garde** — il évite de
 * proposer un geste dont le refus est certain, ce qui est autre chose. La garde est au
 * service, et elle y reste : sur la fiche d'un `orgadmin` ou du compte qui voit tout, **le
 * bloc ne paraît pas du tout**, ces deux rôles n'entrant dans aucune paire.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LES CANDIDATS SONT LES COMPTES DE LA STRUCTURE COURANTE, ET C'EST LE CADRE DE LA
 * SURFACE, PAS UNE DÉCISION DE CET ÉCRAN.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * La liste des comptes de cette surface est **portée par une structure** : le pont de données
 * l'exige — « aucune structure courante n'est choisie : la liste des comptes ne peut pas être
 * rendue sans elle ». Le sélecteur de tête de surface en tient une, et c'est celle-là que ce
 * bloc lit.
 *
 * **Ce n'est pas une borne d'autorisation**, et il faut le dire : `creerUneRelation` ne prend
 * **aucune structure** en paramètre — elle prend deux personnes. Ce que la structure courante
 * décide ici est **ce que l'écran propose**, jamais ce que le serveur accepte. Une relation
 * « extérieure » reste donc possible par la route, et c'est cohérent avec ce que la fiche
 * affiche déjà des relations : « elles appartiennent à la personne, pas à la structure qui la
 * lit ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **UNE RELATION RECRÉÉE EST RÉÉCRITE, ET LE MESSAGE LE DIT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * La réponse porte `reecrite` : la ligne de relation « étant **unique par couple** et
 * **RÉÉCRITE à la recréation** » (Q560). Ce n'est pas un doublon, et ce n'est pas non plus une
 * réouverture de l'ancien fil : `Conversation.inaccessibleSince` reste posé sur celui-ci —
 * « **recréer une relation ROUVRIRAIT l'ancien fil, ce que le corpus refuse expressément** ».
 * **Le message distingue les deux cas** parce que la personne qui agit ne peut pas le deviner :
 * elle croirait avoir créé quand elle a rouvert, et elle chercherait l'ancienne conversation.
 */
type Compte = {
  id: string;
  role: string;
  prenom: string | null;
  nom: string | null;
};

/**
 * **La tranche maximale que le contrat accepte** — `combien` porte `maximum: 100` sur
 * `GET /api/v1/admin/structures/{id}/comptes`. Nommée ici plutôt qu'écrite en chiffre, pour
 * que la borne d'en face soit lisible depuis l'appel qui la subit.
 */
const TRANCHE_MAXIMALE = 100;

/** Les deux rôles qui entrent dans une paire, et le rôle opposé de chacun. */
const OPPOSE: Record<string, 'TUTOR' | 'STUDENT'> = {
  TUTOR: 'STUDENT',
  STUDENT: 'TUTOR',
};

export function CreationDeRelation() {
  const compte = useRecordContext<Compte>();
  const { structureId } = useStructureCourante();
  const notifier = useNotify();
  const rafraichir = useRefresh();
  const { canAccess: peutCreer } = useCanAccess({
    resource: 'relations',
    action: 'creer-une-relation',
  });

  const [choisi, setChoisi] = useState('');
  const [enCours, setEnCours] = useState(false);

  const opposeAttendu = compte === undefined ? undefined : OPPOSE[compte.role];

  /**
   * **La liste des comptes de la structure courante.** Le pont la borne par la structure, et
   * la tranche est **la plus large que le contrat accepte** : ce n'est pas une surface de
   * parcours, c'est le contenu d'un choix. **`enabled` la coupe** quand le bloc ne paraît pas
   * — un rôle hors paire, aucune structure choisie, ou le droit absent —, pour ne pas tirer
   * une liste que personne ne lira.
   *
   * **CETTE LIGNE DEMANDAIT 200, ET LE CONTRAT PLAFONNE À 100 — corrigé le 20260826.** Le
   * paramètre `combien` porte `maximum: 100`, et `donnees.ts` **rabat silencieusement** par
   * `Math.min(perPage, 100)`. Aucune erreur, aucun `400` : la requête partait bornée à 100 et
   * l'écran affichait cent candidats **en ayant l'air d'en afficher tous**. Demander une
   * valeur qu'on ne peut pas obtenir, c'est écrire une intention que rien n'honore — et le
   * rabattage la rendait invisible.
   *
   * **Au-delà de cent comptes dans une structure, le choix est donc incomplet, et l'écran le
   * DIT** plutôt que de le taire. Le corriger au fond demande une recherche dans ce bloc, ou
   * la pagination du sélecteur : c'est une surface de plus, et elle n'est pas de ce lot.
   */
  const candidats = useGetList<Compte>(
    'comptes',
    {
      filter: { structureId },
      pagination: { page: 1, perPage: TRANCHE_MAXIMALE },
      sort: { field: 'id', order: 'ASC' },
    },
    { enabled: peutCreer && opposeAttendu !== undefined && structureId !== null },
  );

  if (compte === undefined || !peutCreer) return null;
  // **Un `orgadmin` et le compte qui voit tout n'entrent dans aucune paire** : le bloc
  // n'existe pas sur leur fiche, plutôt que d'y proposer un geste toujours refusé.
  if (opposeAttendu === undefined) return null;

  const lus = candidats.data ?? [];
  const proposables = lus.filter(
    (ligne) => ligne.role === opposeAttendu && ligne.id !== compte.id,
  );
  // **La tranche est pleine : il y a peut-être une suite, et personne ne peut le deviner.**
  const listeTronquee = lus.length >= TRANCHE_MAXIMALE;

  const nommer = (ligne: Compte) =>
    [ligne.prenom, ligne.nom].filter((x) => x !== null).join(' ') ||
    'Compte anonymisé';

  const poser = async () => {
    if (choisi === '') return;
    setEnCours(true);
    try {
      // **L'ordre des deux champs vient du RÔLE, pas de l'écran.** La route nomme un tuteur et
      // un élève ; c'est la fiche courante qui dit lequel des deux elle est. Et **elle ne
      // porte aucune structure** : la créatrice est celle de l'appelant, ce qui est tout
      // l'objet de l'amendement.
      const paire =
        compte.role === 'TUTOR'
          ? { tuteurId: compte.id, eleveId: choisi }
          : { tuteurId: choisi, eleveId: compte.id };
      const posee = await deballer(creerUneRelation({ body: paire }));
      notifier(
        posee.reecrite
          ? 'Relation rétablie. Sa ligne était déjà là et vient d’être réécrite : ' +
              'l’ancienne conversation reste fermée, une neuve s’ouvre.'
          : 'Relation créée. La conversation existe désormais, sans autre geste.',
        { type: 'info' },
      );
      setChoisi('');
      rafraichir();
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      notifier(
        lue.code === 'PAIRE_DE_RELATION_NON_EXPOSEE'
          ? 'Le produit n’expose que la paire tuteur–élève. Une relation entre deux ' +
              'tuteurs n’existe pas, et la paire élève–élève naît d’un cours groupé.'
          : lue.message,
        { type: 'error' },
      );
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">Mettre en relation</h3>
      <p className="text-muted-foreground text-xs">
        {compte.role === 'TUTOR'
          ? 'Un élève mis en relation avec ce tuteur pourra recevoir un cours de sa part. ' +
            'La conversation s’ouvre du même geste, sans autre action.'
          : 'Un tuteur mis en relation avec cet élève pourra lui ouvrir un cours. ' +
            'La conversation s’ouvre du même geste, sans autre action.'}
      </p>
      <p className="text-muted-foreground text-xs">
        Votre structure sera la structure créatrice de cette relation, et c’est
        elle qui pourra la défaire.
      </p>

      {structureId === null ? (
        <p className="text-muted-foreground text-sm">
          Choisissez une structure dans le sélecteur en tête de la surface pour
          voir les comptes proposables.
        </p>
      ) : proposables.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {compte.role === 'TUTOR'
            ? 'Aucun élève à proposer dans cette structure.'
            : 'Aucun tuteur à proposer dans cette structure.'}
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="relationAvec">
              {compte.role === 'TUTOR' ? 'Élève' : 'Tuteur'}
            </Label>
            {/* **Un `select` natif, et non un composant de la peau.** Ce bloc est une action
                métier, pas un champ de ressource : `DOCTRINE.md` §7 — « une action métier
                custom est **du React ordinaire** ». */}
            <select
              id="relationAvec"
              className="border-input bg-background h-9 rounded-md border px-3 text-sm"
              value={choisi}
              onChange={(evenement) => setChoisi(evenement.target.value)}
            >
              <option value="">—</option>
              {proposables.map((ligne) => (
                <option key={ligne.id} value={ligne.id}>
                  {nommer(ligne)}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={choisi === '' || enCours}
            onClick={() => void poser()}
          >
            Mettre en relation
          </Button>
        </div>
      )}

      {listeTronquee && (
        <p className="text-muted-foreground text-xs">
          Seuls les {TRANCHE_MAXIMALE} premiers comptes de la structure sont
          proposés. Si la personne cherchée n’y est pas, elle existe peut-être
          au-delà de cette tranche.
        </p>
      )}
    </div>
  );
}
